import fetcher from '../../helpers/fetcher';
import { ENVIRONMENT_CONFIG } from '../../constants/environmentConfig';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { unwrapDataResponse } from '../../utils/unwrapDataResponse';
import { indexedDBManager } from '../../utils/indexedDB';
import type {
  CreateProductCategoryDto,
  ProductCategory,
  ProductCategoryQueryParams,
  ProductCategoryResponse,
  UpdateProductCategoryDto,
} from '../../types/productCategory';

class ProductCategoryService {
  private consumerPriceUrl = ENVIRONMENT_CONFIG.CONSUMER_PRICE_SERVICE;
  private analyticsUrl = ENVIRONMENT_CONFIG.ANALYSIS_SERVICE;

  async getProductCategories(
    params: ProductCategoryQueryParams = {}
  ): Promise<ProductCategoryResponse> {
    const queryParams: Record<string, string> = {};

    if (params.page) queryParams.page = params.page.toString();
    if (params.limit) queryParams.limit = params.limit.toString();
    if (params.sortBy) queryParams.sortBy = params.sortBy;
    if (params.sortOrder) queryParams.sortOrder = params.sortOrder;
    if (params.search) queryParams.search = params.search;
    if (params.since) queryParams.since = params.since.toString();
    if (params.status) queryParams.status = params.status;

    const response = await fetcher.get(
      API_ENDPOINTS.PRODUCT_CATEGORY.GET_PRODUCT_CATEGORY_LIST,
      queryParams,
      undefined,
      this.consumerPriceUrl
    );
    return unwrapDataResponse<ProductCategoryResponse>(response.data);
  }

  async getProductCategoryById(id: number): Promise<ProductCategory> {
    const response = await fetcher.get(
      `${API_ENDPOINTS.PRODUCT_CATEGORY.PRODUCT_CATEGORY}/${id}`,
      undefined,
      undefined,
      this.analyticsUrl
    );
    return response.data as ProductCategory;
  }

  async createProductCategory(data: CreateProductCategoryDto): Promise<ProductCategory[]> {
    const response = await fetcher.post(
      API_ENDPOINTS.PRODUCT_CATEGORY.PRODUCT_CATEGORIES,
      data,
      undefined,
      this.consumerPriceUrl
    );
    return response.data as ProductCategory[];
  }

  async updateProductCategory(
    id: number,
    data: UpdateProductCategoryDto
  ): Promise<ProductCategory> {
    const response = await fetcher.patch(
      `${API_ENDPOINTS.PRODUCT_CATEGORY.PRODUCT_CATEGORIES}/${id}`,
      data,
      undefined,
      this.consumerPriceUrl
    );
    return response.data as ProductCategory;
  }

  async deleteProductCategory(id: number): Promise<void> {
    await fetcher.del(
      `${API_ENDPOINTS.PRODUCT_CATEGORY.PRODUCT_CATEGORIES}/${id}`,
      undefined,
      undefined,
      this.consumerPriceUrl
    );

    await indexedDBManager.updateLocalData('productCategories', String(id), {
      isActive: false,
      updateTime: Date.now(),
    });
  }

  async restoreProductCategory(id: number): Promise<void> {
    await fetcher.patch(
      `${API_ENDPOINTS.PRODUCT_CATEGORY.RESTORE_PRODUCT_CATEGORY}/${id}/restore`,
      {},
      undefined,
      this.consumerPriceUrl
    );

    await indexedDBManager.updateLocalData('productCategories', String(id), {
      isActive: true,
      updateTime: Date.now(),
    });
  }
}

export const productCategoryService = new ProductCategoryService();
